import { storage } from '../../firebase'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { TenantService } from './tenants'

const VALID_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']
const EMBEDDED_LOGO_MAX_DIMENSION = 320
const EMBEDDED_LOGO_QUALITY = 0.82

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onload = () => resolve(reader.result)
  reader.onerror = () => reject(new Error('Failed to read logo file'))
  reader.readAsDataURL(file)
})

const resizeImageForEmbedding = async (file) => {
  const dataUrl = await readFileAsDataUrl(file)

  if (file.type === 'image/svg+xml') {
    return dataUrl
  }

  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => {
      const ratio = Math.min(
        1,
        EMBEDDED_LOGO_MAX_DIMENSION / image.width,
        EMBEDDED_LOGO_MAX_DIMENSION / image.height
      )

      const canvas = document.createElement('canvas')
      canvas.width = Math.max(1, Math.round(image.width * ratio))
      canvas.height = Math.max(1, Math.round(image.height * ratio))

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Failed to prepare logo canvas'))
        return
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
      resolve(canvas.toDataURL('image/png', EMBEDDED_LOGO_QUALITY))
    }
    image.onerror = () => reject(new Error('Failed to process logo image'))
    image.src = dataUrl
  })
}

const saveLogoToTenantSettings = async (tenantId, logoUrl, logoStorageType = 'firebase-storage') => {
  const tenantResult = await TenantService.getTenantById(tenantId)

  if (tenantResult.error) {
    throw new Error('Failed to load tenant settings: ' + tenantResult.error.message)
  }

  const updateResult = await TenantService.updateTenantSettings(tenantId, {
    ...(tenantResult.data?.settings || {}),
    logoUrl,
    logoStorageType
  })

  if (updateResult.error) {
    throw new Error('Failed to update tenant settings: ' + updateResult.error.message)
  }
}

/**
 * Upload a tenant logo to Firebase Storage
 * @param {string} tenantId - The tenant ID
 * @param {File} file - The logo file to upload
 * @returns {Promise<{success: boolean, downloadURL?: string, error?: string}>}
 */
export async function uploadTenantLogo(tenantId, file) {
  try {
    // Validate inputs
    if (!tenantId) {
      throw new Error('Tenant ID is required')
    }
    
    if (!file) {
      throw new Error('File is required')
    }
    
    // Validate file type (accept common image formats)
    if (!VALID_IMAGE_TYPES.includes(file.type)) {
      throw new Error('Invalid file type. Please upload an image (JPEG, PNG, GIF, WebP, or SVG)')
    }
    
    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024 // 5MB in bytes
    if (file.size > maxSize) {
      throw new Error('File size exceeds 5MB limit')
    }
    
    console.log(`📤 Uploading logo for tenant ${tenantId}...`)
    console.log(`📁 File: ${file.name} (${(file.size / 1024).toFixed(2)} KB)`)
    
    // Use an embedded logo path for now to avoid the production Firebase Storage CORS failure.
    // This keeps white-label branding working immediately for live accounts.
    console.log('💾 Saving logo using embedded tenant branding mode...')
    const embeddedLogoUrl = await resizeImageForEmbedding(file)
    await saveLogoToTenantSettings(tenantId, embeddedLogoUrl, 'embedded-data-url')

    return {
      success: true,
      downloadURL: embeddedLogoUrl,
      fallbackUsed: true
    }
    
  } catch (error) {
    console.error('❌ Logo upload failed:', error)
    return {
      success: false,
      error: error.message || 'Unknown error occurred during logo upload'
    }
  }
}

/**
 * Delete a tenant logo from Firebase Storage
 * @param {string} tenantId - The tenant ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function deleteTenantLogo(tenantId) {
  try {
    if (!tenantId) {
      throw new Error('Tenant ID is required')
    }
    
    console.log(`🗑️ Deleting logo for tenant ${tenantId}...`)
    
    // Update tenant settings to remove logo URL
    const tenantResult = await TenantService.getTenantById(tenantId)
    
    if (tenantResult.error) {
      throw new Error('Failed to load tenant settings: ' + tenantResult.error.message)
    }
    
    const updateResult = await TenantService.updateTenantSettings(tenantId, {
      ...(tenantResult.data?.settings || {}),
      logoUrl: null,
      logoStorageType: null
    })
    
    if (updateResult.error) {
      throw new Error('Failed to update tenant settings: ' + updateResult.error.message)
    }
    
    console.log('✅ Tenant logo removed successfully')
    
    return {
      success: true
    }
    
  } catch (error) {
    console.error('❌ Logo deletion failed:', error)
    return {
      success: false,
      error: error.message || 'Unknown error occurred during logo deletion'
    }
  }
}

export default {
  uploadTenantLogo,
  deleteTenantLogo
}
